import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

export default function Contact() {
  const { t } = useLang();
  const C = t.contact;
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    domain: C.domains[0],
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 4) {
      toast.error(C.validation);
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success(C.success);
      setForm({ name: "", email: "", organization: "", domain: C.domains[0], message: "" });
    } catch (err) {
      // Surface a more diagnostic message so production issues are debuggable
      let msg = C.error;
      if (err?.response?.data?.detail) {
        msg = typeof err.response.data.detail === "string"
          ? err.response.data.detail
          : C.error;
      } else if (err?.response?.status) {
        msg = `${C.error} (HTTP ${err.response.status})`;
      } else if (err?.code === "ECONNABORTED") {
        msg = `${C.error} (timeout)`;
      } else if (err?.message) {
        msg = `${C.error} (${err.message})`;
      }
      console.error("[CATT] Contact submit failed:", err);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-b border-white/10 py-28 lg:py-36"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-6 lg:gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="col-span-12 lg:col-span-5"
        >
          <div className="label-mono mb-4">{C.label}</div>
          <h2 className="display-2 text-4xl md:text-5xl lg:text-6xl text-white">
            {C.title_a}
            <br />
            <span className="text-white/40">{C.title_muted}</span>{" "}
            <span className="text-[#FF3333]">{C.title_accent}</span>
          </h2>

          <p className="mt-8 text-white/70 text-base leading-relaxed max-w-md">{C.body}</p>

          <div className="mt-12 space-y-5 font-mono text-xs tracking-widest uppercase text-white/50">
            <div className="flex items-center gap-4">
              <span className="text-[#FF3333]">↳</span>
              <a href="mailto:segeberg@kmu.ac.kr" data-testid="contact-email" className="hover:text-white">
                segeberg@kmu.ac.kr
              </a>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#FF3333]">↳</span>
              <span>053-580-8980</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-[#FF3333]">↳</span>
              <span>CATT · Keimyung University</span>
            </div>
          </div>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="col-span-12 lg:col-span-7 border border-white/15 bg-[#1C1C1E] p-8 lg:p-12"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label={C.name} required testid="contact-input-name" value={form.name} onChange={onChange("name")} placeholder="Dr. Jane Park" />
            <Field label={C.email} required type="email" testid="contact-input-email" value={form.email} onChange={onChange("email")} placeholder="jane@hospital.org" />
            <Field label={C.org} testid="contact-input-org" value={form.organization} onChange={onChange("organization")} placeholder="Acme Medical, Inc." />
            <div>
              <label className="label-mono block mb-2">{C.domain}</label>
              <select
                data-testid="contact-select-domain"
                value={form.domain}
                onChange={onChange("domain")}
                className="w-full bg-[#101012] border border-white/15 px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF3333] transition-colors"
              >
                {C.domains.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-6">
            <label className="label-mono block mb-2">{C.message}</label>
            <textarea
              data-testid="contact-input-message"
              value={form.message}
              onChange={onChange("message")}
              required
              rows={6}
              placeholder={C.message_placeholder}
              className="w-full bg-[#101012] border border-white/15 px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF3333] transition-colors resize-none placeholder:text-white/30"
            />
          </div>

          <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
            <span className="font-mono text-[10px] tracking-widest uppercase text-white/40">
              {C.nda}
            </span>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 bg-[#FF3333] hover:bg-[#E60000] disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-4 font-mono text-xs tracking-[0.18em] uppercase transition-colors"
            >
              {loading ? (<><Loader2 size={16} className="animate-spin" />{C.sending}</>) : (<>{C.send}<Send size={14} className="group-hover:translate-x-1 transition-transform" /></>)}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}

function Field({ label, testid, type = "text", value, onChange, placeholder, required }) {
  return (
    <div>
      <label className="label-mono block mb-2">
        {label} {required && <span className="text-[#FF3333]">*</span>}
      </label>
      <input
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full bg-[#101012] border border-white/15 px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF3333] transition-colors placeholder:text-white/30"
      />
    </div>
  );
}
